import {signal} from '@angular/core';

// The color of a customer is set on the company (Bedrijven, field Kleur). The app loads all colors after login
// (companies/colors); customers without a color, and work without a company, are grey.

const CUSTOMER_COLORS = signal<Record<number, string>>({});

/** Grey for all other customers and for work without a company. */
export const CUSTOMER_COLOR_NONE = '#9aa0a6';

/** The loaded colors by company id. */
export const customerColors = CUSTOMER_COLORS.asReadonly();

export function setCustomerColors(colors: Record<number, string> | null | undefined) {
    CUSTOMER_COLORS.set(colors ?? {});
}

/** A #rrggbb color as the company form accepts it. */
export function isHexColor(value: string | null | undefined): boolean {
    return /^#[0-9a-f]{6}$/i.test(value ?? '');
}

/** The customer color for dots, blocks and bars. */
export function customerColor(id: number | string | null | undefined): string {
    if (id == null || id === '') {
        return CUSTOMER_COLOR_NONE;
    }
    const color = CUSTOMER_COLORS()[Number(id)];
    return isHexColor(color) ? color : CUSTOMER_COLOR_NONE;
}

/** Light background in the customer color, for tags. */
export function customerTint(id: number | string | null | undefined): string {
    return `color-mix(in srgb, ${customerColor(id)} 14%, #fff)`;
}

/** Text in the customer color; light colors (e.g. yellow) are darkened so they stay readable on white. */
export function customerInk(id: number | string | null | undefined): string {
    const color = customerColor(id);
    return luminance(color) > .3 ? `color-mix(in srgb, ${color} 50%, #000)` : color;
}

/** Relative luminance (WCAG) of a #rrggbb color. */
function luminance(hex: string): number {
    const [r, g, b] = [1, 3, 5]
        .map(i => parseInt(hex.slice(i, i + 2), 16) / 255)
        .map(c => c <= .03928 ? c / 12.92 : ((c + .055) / 1.055) ** 2.4);
    return .2126 * r + .7152 * g + .0722 * b;
}
